import { exception } from 'circe/logger';
import { getCompanyCode } from 'domain/fuse/auth/selectors';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import {
  all, takeLatest, call, select, put, takeEvery,
} from 'redux-saga/effects';
import * as I18n from 'circe/I18n';
import { show as showNotification } from 'circe/notifications/actions';
import { makeProfileDone, revokeProfileDone } from 'domain/operations/accountManagement/actions';
import { changeData } from 'circe/queryListing/actions';
import { getResults } from 'circe/queryListing/selectors';
import discountSaga from 'domain/operations/shippers/discounts/saga';
import * as api from './api';
import {
  fetch, fetchNext, fetchPrev, fetching, fetched, cache,
} from './actions';
import { getShipperWithProfiles } from './utils';
import {
  getNextCursor, getHasNext, getHasPrev, getPrevCursor, getFromCache,
} from './selectors';

import { fetchProfiles } from './api';
import { REDUCER_NAME } from './constants';

function* doFetch(context, { payload }) {
  try {
    const { filters, append } = payload;

    const state = yield select();

    const headers = yield call(getCurrentAuthHeader, state);

    // new scope
    {
      const cache = yield call(getFromCache, state, filters.cursor);

      if (cache && cache.data) {
        const { data, ...meta } = cache;
        yield put(fetched(data, meta));

        return;
      }
    }

    const resp = yield call(
      api.fetch,
      {
        ...filters,
        cursor: filters.cursor,
        excludeDeactivated: true,
        did: SYSTEM_DOMAIN,
      },
      headers,
    );

    const meta = {
      append,
      cursor: filters.cursor,
      nextCursor: resp.nextPage && resp.nextPage.hasResults ? resp.nextPage.cursor : null,
      prevCursor: resp.prevPage && resp.prevPage.hasResults ? resp.prevPage.cursor : null,
    };

    yield put(fetched(resp.results.map(getShipperWithProfiles), meta));

    yield put(
      cache(meta.nextCursor, {
        prevCursor: filters.cursor,
      }),
    );

    yield put(
      cache(filters.cursor, {
        data: resp.results,
        ...meta,
        prevCursor: !filters.cursor ? undefined : meta.prevCursor,
      }),
    );
  } catch (e) {
    yield put(
      showNotification(I18n.str('shippersFetchFailed', 'Unable to load shippers.'), 'error'),
    );
    yield put(fetched());
    exception(e);
  }
}

export function* doFetchNext(context, { payload }) {
  const { append } = payload;

  const state = yield select();

  const hasNextPage = yield call(getHasNext, state);

  if (!hasNextPage) {
    return;
  }

  const cursor = yield call(getNextCursor, state);

  yield put(fetching(append));

  yield call(doFetch, context, fetch({ cursor }, append));
}

export function* doFetchPrev(context) {
  const state = yield select();

  const hasPrevPage = yield call(getHasPrev, state);

  if (!hasPrevPage) {
    return;
  }

  const cursor = yield call(getPrevCursor, state);

  yield put(fetching());

  yield call(doFetch, context, fetch({ cursor }));
}

function* doUpdateShipperOnProfileChange(context, action) {
  const state = yield select();

  const { payload } = action;

  const { id } = payload;

  const headers = yield call(getCurrentAuthHeader, state);

  const profiles = yield call(fetchProfiles, id, headers);

  const shippers = yield call(getResults, state, REDUCER_NAME);

  const data = shippers.map((shipper) => {
    if (
      shipper.id === id
      || Boolean((shipper.profiles || []).find((prof) => prof.profileId === id))
    ) {
      return getShipperWithProfiles({
        ...shipper,
        profiles,
      });
    }
    return shipper;
  });

  yield put(changeData(REDUCER_NAME, data));
}

export default function* (context) {
  yield all([
    takeLatest(fetch().type, doFetch, context),
    takeLatest(fetchNext().type, doFetchNext, context),
    takeLatest(fetchPrev().type, doFetchPrev, context),
    takeEvery(makeProfileDone().type, doUpdateShipperOnProfileChange, context),
    takeEvery(revokeProfileDone().type, doUpdateShipperOnProfileChange, context),
    discountSaga(),
  ]);
}
